import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Trash2, Eye, EyeOff, Edit2, Loader2, Quote } from 'lucide-react';
import EditReviewModal from './EditReviewModal';

const AdminReviewList = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingReview, setEditingReview] = useState(null);

    const fetchReviews = async () => {
        try {
            setLoading(true);
            const response = await api.get('/reviews/admin');
            setReviews(response.data);
            setError('');
        } catch (err) {
            console.error('Failed to fetch reviews:', err);
            setError('Failed to load reviews.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviews();
    }, []);

    const toggleVisibility = async (review) => {
        try {
            const updatedReview = await api.patch(`/reviews/${review._id}`, {
                isVisible: !review.isVisible
            });
            // Update local state
            setReviews(reviews.map(r => r._id === review._id ? updatedReview.data : r));
        } catch (err) {
            console.error('Failed to update visibility:', err);
            setError('Failed to update review visibility.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this review?')) return;
        try {
            await api.delete(`/reviews/${id}`);
            setReviews(reviews.filter(r => r._id !== id));
        } catch (err) {
            console.error('Failed to delete review:', err);
            setError('Failed to delete review.');
        }
    };

    const handleReviewUpdated = (updatedReview) => {
        setReviews(reviews.map(r => r._id === updatedReview._id ? updatedReview : r));
        setEditingReview(null);
    };

    if (loading) {
        return (
            <div className="flex justify-center p-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (reviews.length === 0) {
        return (
            <div className="text-center p-8 text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                No reviews found.
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-800">Manage Reviews</h3>

            {error && (
                <div className="p-4 bg-red-50 text-red-800 rounded-lg text-sm mb-4">
                    {error}
                </div>
            )}

            <div className="grid gap-4">
                {reviews.map((review) => (
                    <div key={review._id} className={`bg-white p-4 rounded-xl shadow-sm border ${review.isVisible ? 'border-gray-100' : 'border-red-100 bg-red-50/10'} flex flex-col md:flex-row items-center gap-4 transition-all hover:shadow-md`}>
                        {/* Review Content */}
                        <div className="flex items-center gap-4 flex-1 w-full">
                            <img
                                src={review.image}
                                alt={review.name}
                                className="w-12 h-12 rounded-full object-cover ring-2 ring-gray-100 flex-shrink-0"
                            />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-bold text-gray-900 truncate">{review.name}</h4>
                                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{review.role}</span>
                                    {!review.isVisible && (
                                        <span className="text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded-full font-medium">Hidden</span>
                                    )}
                                </div>
                                <div className="flex gap-2 text-gray-600 text-sm">
                                    <Quote className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                                    <p className="line-clamp-2">{review.text}</p>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 w-full md:w-auto justify-end border-t md:border-t-0 pt-4 md:pt-0 mt-2 md:mt-0">
                            <button
                                onClick={() => setEditingReview(review)}
                                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit Review"
                            >
                                <Edit2 className="w-5 h-5" />
                            </button>

                            <button
                                onClick={() => toggleVisibility(review)}
                                className={`p-2 rounded-lg transition-colors ${review.isVisible ? 'text-gray-500 hover:text-orange-600 hover:bg-orange-50' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`}
                                title={review.isVisible ? "Hide Review" : "Show Review"}
                            >
                                {review.isVisible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                            </button>

                            <button
                                onClick={() => handleDelete(review._id)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete Review"
                            >
                                <Trash2 className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <EditReviewModal
                isOpen={!!editingReview}
                onClose={() => setEditingReview(null)}
                review={editingReview}
                onReviewUpdated={handleReviewUpdated}
            />
        </div>
    );
};

export default AdminReviewList;
